import {
  VESSEL_CARD_SCRUBBER_CAPES_ALLOWANCE,
  VESSEL_CARD_SCRUBBER_CAPES_MIN_DWT,
  VESSEL_CARD_SCRUBBER_CONSUMPTION_FACTOR,
  VESSEL_CARD_SCRUBBER_HANDY_SUPRAMAX_ALLOWANCE,
  VESSEL_CARD_SCRUBBER_KAMSARMAX_ALLOWANCE,
  VESSEL_CARD_SCRUBBER_KAMSARMAX_MIN_DWT,
  VESSEL_CARD_SCRUBBER_MAX_DWT,
  VESSEL_CARD_SCRUBBER_MIN_DWT,
} from "../constants/vessel-card.constants";
import type { VesselCardDashboard, VesselCardPerformanceRow } from "../models/vessel-card.models";

// Assumes the agreed correction applies to all four design consumption rows, leaving their speeds unchanged.
// Always starts from the loaded VLSFO card; repeated toggles cannot add the correction more than once.
// OBS_Actual stays unchanged, and No Data cannot acquire expected figures from a CP source or stale fixture.
export const applyVesselCardScrubberSelection = (
  card: VesselCardDashboard,
  scrubberSelected: boolean,
): VesselCardDashboard => {
  const basis = card.performanceBasis?.trim().toLowerCase();
  const hasNoData = basis === "no data" || basis === "no_data";
  const needsDesignCorrection = basis?.startsWith("design") && scrubberSelected;

  if (!hasNoData && !needsDesignCorrection) {
    return card;
  }

  const allowance = getVesselCardScrubberAllowance(card.summerDwt);
  const performance = card.performance.map(
    (row): VesselCardPerformanceRow => ({
      ...row,
      expectedSpeed: hasNoData ? null : row.expectedSpeed,
      expectedConsumption: hasNoData ? null : toHsfoDesignConsumption(row.expectedConsumption, allowance),
      // Drops pre-correction CP results; the CP source adapter recalculates them from these corrected expected inputs.
      // CP description values remain as entered, while every comparison uses the same corrected expected rows.
      expectedAtCpSpeed: null,
      expectedAtCpConsumption: null,
      indicator: "Not available",
      indicatorTone: "neutral",
    }),
  );

  return {
    ...card,
    performance,
    // TCO keeps its existing speed treatment and uses the same expected consumption as the comparison tables.
    tcoDescriptions: card.tcoDescriptions.map((row) => {
      const expectedConsumption =
        performance.find((expected) => expected.mode === row.mode)?.expectedConsumption ?? null;

      return {
        ...row,
        aggressiveSpeed: hasNoData ? null : row.aggressiveSpeed,
        veryAggressiveSpeed: hasNoData ? null : row.veryAggressiveSpeed,
        aggressiveConsumption: expectedConsumption,
        veryAggressiveConsumption: expectedConsumption,
      };
    }),
  };
};

// === SCRUBBER INFORMATION ===

// Selects the agreed DWT allowance without guessing for missing or unsupported vessel sizes.
export const getVesselCardScrubberAllowance = (summerDwt: number | null | undefined): number | null => {
  if (
    summerDwt === null ||
    summerDwt === undefined ||
    !Number.isFinite(summerDwt) ||
    summerDwt < VESSEL_CARD_SCRUBBER_MIN_DWT ||
    summerDwt > VESSEL_CARD_SCRUBBER_MAX_DWT
  ) {
    return null;
  }

  if (summerDwt < VESSEL_CARD_SCRUBBER_KAMSARMAX_MIN_DWT) {
    return VESSEL_CARD_SCRUBBER_HANDY_SUPRAMAX_ALLOWANCE;
  }

  return summerDwt < VESSEL_CARD_SCRUBBER_CAPES_MIN_DWT
    ? VESSEL_CARD_SCRUBBER_KAMSARMAX_ALLOWANCE
    : VESSEL_CARD_SCRUBBER_CAPES_ALLOWANCE;
};

// Explains the actual-vessel lock and the separate Design and No Data behavior beside the selector.
export const getVesselCardScrubberSelectionHelp = (card: VesselCardDashboard | null): string => {
  const basis = card?.performanceBasis?.trim().toLowerCase();

  if (!basis || (basis === "obs_actual" && card?.scrubberFitted === null)) {
    return "Scrubber information unavailable.";
  }

  if (basis === "obs_actual") {
    return "Read-only: IMOS scrubber information.";
  }

  if (basis.startsWith("design")) {
    return "No uses VLSFO design consumption. Yes applies the HSFO scrubber correction; expected speed stays unchanged.";
  }

  return "Changes the fuel-grade label only; expected performance remains unavailable.";
};

// Names both source records for a conflict and makes an unavailable IMOS value or DWT visible to the trader.
export const getVesselCardScrubberWarning = (card: VesselCardDashboard, scrubberSelected: boolean): string | null => {
  const basis = card.performanceBasis?.trim().toLowerCase();
  const information = card.scrubberInformation;

  if (basis === "obs_actual") {
    if (information?.scrubberConflict) {
      const imosValue = information.scrubberImosFitted ? "Yes" : "No";
      const summaryValue = information.scrubberPerformanceSummaryFitted ? "Yes" : "No";
      const imosType = information.scrubberImosType ? ` (${information.scrubberImosType})` : "";

      return `Scrubber conflict: IMOS reports ${imosValue}${imosType}; performance summary reports ${summaryValue}. IMOS is used. Correct the conflicting source record.`;
    }

    return card.scrubberFitted === null
      ? "IMOS scrubber information is unavailable. Fuel grade remains unavailable until IMOS is corrected."
      : null;
  }

  if (basis?.startsWith("design") && scrubberSelected && getVesselCardScrubberAllowance(card.summerDwt) === null) {
    return "HSFO correction is unavailable: vessel DWT must be known and between 18,000 and 220,000. Expected consumption remains unavailable.";
  }

  return null;
};

const toHsfoDesignConsumption = (consumption: number | null, allowance: number | null): number | null => {
  if (consumption === null || !Number.isFinite(consumption) || consumption <= 0 || allowance === null) {
    return null;
  }

  const correctedConsumption = consumption * VESSEL_CARD_SCRUBBER_CONSUMPTION_FACTOR + allowance;

  return Number.isFinite(correctedConsumption) ? correctedConsumption : null;
};
